import { StorageDeadError, StorageTimeoutError } from "@beresta/core";
import { isFatal } from "./protocol.js";
/**
 * Клієнт бази у вебі. Сама SQLite живе у воркері, бо `FileSystemSyncAccessHandle`
 * експонований лише там ([спайк](../../../../spikes/opfs-browser/README.md)).
 *
 * Уся цінність цього файлу — у відмовах. Обмін повідомленнями ламається тихо:
 * воркер може впасти, зависнути або не піднятися взагалі, і за замовчуванням
 * застосунок просто перестане відповідати, ні про що не повідомивши.
 */
export const DEFAULT_TIMEOUT_MS = 10_000;
/** Міграція на великій базі — довша операція, і обривати її небезпечно. */
export const MIGRATE_TIMEOUT_MS = 120_000;
/**
 * `open` — не запит, а холодний старт: завантажити мегабайт wasm, скомпілювати
 * його й захопити файли VFS. Ставити йому таймаут звичайного запиту було
 * помилкою, і вона проявлялася як випадкові падіння під навантаженням.
 *
 * Заміряно: без навантаження весь старт триває близько 500 мс, під вісьмома
 * паралельними вкладками — 2850. Тобто запас до десяти секунд був менш ніж
 * чотирикратний, і на завантаженій машині його не вистачало.
 *
 * Тридцять секунд — це не «побільше про всяк випадок», а порядок величини
 * для завантаження й компіляції wasm на повільному зʼєднанні. Верхню межу
 * тримає окремий дедлайн усього запуску в застосунку.
 */
export const OPEN_TIMEOUT_MS = 30_000;
export class WebDb {
    #worker;
    #pending = new Map();
    #next = 1;
    #state = "starting";
    #deathReason = "";
    #onDeath;
    constructor(worker, opts = {}) {
        this.#worker = worker;
        if (opts.onDeath)
            this.#onDeath = opts.onDeath;
        worker.addEventListener("message", (e) => {
            if (isFatal(e.data))
                return this.#die(e.data.fatal);
            this.#settle(e.data);
        });
        // Воркер, що впав, більше нічого не надішле — про це має дізнатися кожен,
        // хто чекає, а не лише той, чий таймер спрацює першим.
        worker.addEventListener("error", (e) => {
            this.#die(typeof e.message === "string" && e.message ? e.message : "воркер завершився з помилкою");
        });
        worker.addEventListener("messageerror", () => {
            this.#die("повідомлення воркера не вдалося прочитати");
        });
    }
    get state() {
        return this.#state;
    }
    #die(reason) {
        if (this.#state === "dead")
            return;
        this.#state = "dead";
        this.#deathReason = reason;
        const err = new StorageDeadError(reason);
        for (const p of this.#pending.values()) {
            clearTimeout(p.timer);
            p.reject(err);
        }
        this.#pending.clear();
        this.#onDeath?.(reason);
    }
    #settle(r) {
        const p = this.#pending.get(r.id);
        if (!p)
            return; // відповідь на запит, який уже впав за таймаутом
        clearTimeout(p.timer);
        this.#pending.delete(r.id);
        if (this.#state === "starting")
            this.#state = "ready";
        p.resolve(r);
    }
    #send(req, timeoutMs = DEFAULT_TIMEOUT_MS) {
        if (this.#state === "dead") {
            return Promise.reject(new StorageDeadError(this.#deathReason));
        }
        const id = this.#next++;
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                this.#pending.delete(id);
                // Таймаут — не смерть: одна повільна відповідь не має ховати базу.
                // Смерть оголошує лише подія від самого воркера.
                reject(new StorageTimeoutError(req.op, timeoutMs));
            }, timeoutMs);
            this.#pending.set(id, { resolve, reject, timer, op: req.op });
            this.#worker.postMessage({ ...req, id });
        });
    }
    async #call(req, timeoutMs) {
        const r = await this.#send(req, timeoutMs);
        if (!r.ok)
            throw new Error(r.error);
        return r;
    }
    /**
     * Відкриває базу. `wasmUrl` — адреса `sqlite3.wasm` у тому вигляді, у якому
     * її роздає застосунок: обгортка sqlite3 сама шукає wasm поруч зі своїм JS,
     * а в зібраному застосунку це не той каталог.
     */
    async open(dbName, wasmUrl) {
        await this.#call({ op: "open", dbName, ...(wasmUrl ? { wasmUrl } : {}) }, OPEN_TIMEOUT_MS);
    }
    /**
     * Чи означає ця помилка «файли сховища тримає хтось інший».
     *
     * Текст різниться між рушіями: Chromium каже `NoModificationAllowedError`
     * і згадує `createSyncAccessHandle`, Safari — коротке «The object is in
     * an invalid state». Спільного коду помилки немає, тож розпізнаємо за
     * обома формами. Помилитися тут дешево в один бік і дорого в інший:
     * зайвий повтор коштує пів секунди, а нерозпізнана зайнятість —
     * застосунок, який виглядає зламаним.
     */
    static busy(reason) {
        return (reason.includes("NoModificationAllowedError") ||
            reason.includes("createSyncAccessHandle") ||
            reason.includes("invalid state"));
    }
    async migrate() {
        await this.#call({ op: "migrate" }, MIGRATE_TIMEOUT_MS);
    }
    async exec(sql, params) {
        await this.#call(params ? { op: "exec", sql, params } : { op: "exec", sql });
    }
    async query(sql, params) {
        const r = await this.#call(params ? { op: "query", sql, params } : { op: "query", sql });
        return (r.ok ? (r.rows ?? []) : []);
    }
    /**
     * Транзакція. Вкладені транзакції не підтримуються навмисно: SQLite їх
     * має лише через savepoint, а мовчазне зведення вкладеної до зовнішньої
     * дає відкат, якого ніхто не очікував.
     */
    async transaction(fn) {
        await this.exec("BEGIN");
        try {
            const out = await fn(this);
            await this.exec("COMMIT");
            return out;
        }
        catch (cause) {
            // Відкат не має ховати первинну помилку: якщо база вже мертва,
            // ROLLBACK теж упаде, а знати треба про причину.
            await this.exec("ROLLBACK").catch(() => undefined);
            throw cause;
        }
    }
    async close() {
        if (this.#state !== "dead") {
            await this.#call({ op: "close" }).catch(() => undefined);
        }
        this.#worker.terminate();
        this.#die("закрито застосунком");
    }
}
//# sourceMappingURL=client.js.map